import React, { isValidElement, memo, useMemo } from 'react';
import { StyleSheet, TouchableOpacity, View, } from 'react-native';
import Animated, { interpolate, interpolateColor, useAnimatedStyle, } from 'react-native-reanimated';
import { styles as textStyles } from 'design-system/Text';
import useColors from 'hooks/useColors';
import { rem } from './values';
export const DEFAULT_SPACE_BETWEEN = rem(8);
const SelectorControllableReanimated = React.memo(({ sections, style, selectorStyle, itemContainerStyle, itemStyle, scrollX, screenWidth, setSelection, spaceBetween = DEFAULT_SPACE_BETWEEN, withBackground, }) => {
    const itemsLayoutsTranslates = useMemo(() => {
        if (sections.length < 2) {
            return [0, 0];
        }
        const layouts = sections.map((sc) => sc.width || 0);
        return layouts.map((_, index) => layouts.slice(0, index).reduce((cv, sum) => sum + cv + spaceBetween, 0));
    }, [sections, spaceBetween]);
    const interpolateInput = useMemo(() => Array(sections.length < 2 ? 2 : sections.length)
        .fill(0)
        .map((_, i) => i * screenWidth), [screenWidth, sections.length]);
    const colors = useColors();
    const defaultSelectorColor = colors.buttons.brandGroup;
    const backgroundColors = useMemo(() => {
        if (sections.length < 2) {
            return [
                sections[0]?.color || defaultSelectorColor,
                defaultSelectorColor,
            ];
        }
        return sections.map((s) => s.color || defaultSelectorColor);
    }, [defaultSelectorColor, sections]);
    const widthOutput = useMemo(() => Array(sections.length < 2 ? 2 : sections.length)
        .fill(0)
        .map((_, i) => {
        const sc = sections[i];
        return sc ? sc.width || 0 : 0;
    }), [sections]);
    const transformStyle = useAnimatedStyle(() => ({
        width: interpolate(scrollX.value, interpolateInput, widthOutput),
        transform: [
            {
                translateX: interpolate(scrollX.value, interpolateInput, itemsLayoutsTranslates, 'clamp'),
            },
        ],
        backgroundColor: interpolateColor(scrollX.value, interpolateInput, backgroundColors),
    }), [
        scrollX,
        interpolateInput,
        widthOutput,
        itemsLayoutsTranslates,
        backgroundColors,
    ]);
    const lastIndex = sections.length - 1;
    return (<View style={[styles.container, style]}>
        {withBackground ? (<View style={[
                styles.itemContainer,
                StyleSheet.absoluteFill,
                itemContainerStyle,
            ]}>
            {sections.map((section, index) => (<View key={section.id} style={[
                    styles.defaultPadding,
                    itemStyle,
                    {
                        marginRight: index === lastIndex ? 0 : spaceBetween,
                        width: section.width,
                        backgroundColor: colors.cards.onDark,
                    },
                ]}/>))}
          </View>) : null}
        <Animated.View style={[styles.selector, selectorStyle, transformStyle]}/>
        <View style={[styles.itemContainer, itemContainerStyle]}>
          {sections.map((section, index) => (<TouchableOpacity onPress={() => setSelection(index, section)} key={section.id}>
              <View style={[
                styles.item,
                {
                    marginRight: index === lastIndex ? 0 : spaceBetween,
                    width: section.width,
                },
                styles.defaultPadding,
                itemStyle,
            ]}>
                <View style={styles.itemInnerContainer}>
                  {section.leftIcon ? (<View style={styles.leftIconWrapper}>
                      <SelectorIcon scrollX={scrollX} index={index} screenWidth={screenWidth} selectedColor={section.selectedTextColor ??
                    colors.text.textOnBrand} unselectedColor={section.unSelectedTextColor ??
                    colors.text.secondary}>
                        {section.leftIcon}
                      </SelectorIcon>
                    </View>) : null}
                  <SelectorText scrollX={scrollX} index={index} screenWidth={screenWidth} selectedColor={section.selectedTextColor ??
                colors.text.textOnBrand} unselectedColor={section.unSelectedTextColor ??
                colors.text.secondary}>
                    {section.text}
                  </SelectorText>
                  {section.rightIcon ? (<View style={styles.rightIconWrapper}>
                      <SelectorIcon scrollX={scrollX} index={index} screenWidth={screenWidth} selectedColor={section.selectedTextColor ??
                    colors.text.textOnBrand} unselectedColor={section.unSelectedTextColor ??
                    colors.text.secondary}>
                        {section.rightIcon}
                      </SelectorIcon>
                    </View>) : null}
                </View>
                {section.notificationCount}
              </View>
            </TouchableOpacity>))}
        </View>
      </View>);
});
export const SelectorText = React.memo(({ index, children, scrollX, screenWidth, selectedColor, unselectedColor, }) => {
    const inputRange = useMemo(() => [
        screenWidth * index - screenWidth * 0.95,
        screenWidth * index,
        screenWidth * index + screenWidth * 1.05,
    ], [index, screenWidth]);
    const style = useAnimatedStyle(() => ({
        color: interpolateColor(scrollX.value, inputRange, [
            unselectedColor,
            selectedColor,
            unselectedColor,
        ]),
    }), [scrollX, inputRange, unselectedColor, selectedColor]);
    return (<Animated.Text style={[textStyles.Text_14, style]} maxFontSizeMultiplier={2} numberOfLines={1}>
        {children}
      </Animated.Text>);
});
export const SelectorIcon = React.memo(({ index, children, scrollX, screenWidth, selectedColor, unselectedColor, }) => {
    const inputRange = useMemo(() => [
        screenWidth * index - screenWidth * 0.95,
        screenWidth * index,
        screenWidth * index + screenWidth * 1.05,
    ], [index, screenWidth]);
    const selectedStyle = useAnimatedStyle(() => ({
        opacity: interpolate(scrollX.value, inputRange, [0, 1, 0], 'clamp'),
    }), [scrollX, inputRange]);
    if (!isValidElement(children)) {
        return <View>{children}</View>;
    }
    const iconElement = children;
    return (<View>
        {React.cloneElement(iconElement, { color: unselectedColor })}
        <Animated.View style={[StyleSheet.absoluteFill, selectedStyle]}>
          {React.cloneElement(iconElement, { color: selectedColor })}
        </Animated.View>
      </View>);
});
export default memo(SelectorControllableReanimated);
const styles = StyleSheet.create({
    container: {
        width: '100%',
        borderRadius: rem(20),
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'row',
        paddingVertical: rem(2),
    },
    selector: {
        position: 'absolute',
        left: rem(0),
        top: rem(0),
        bottom: rem(0),
    },
    item: {
        borderRadius: rem(28),
        paddingVertical: rem(8),
        alignItems: 'center',
        justifyContent: 'center',
    },
    itemContainer: {
        flexDirection: 'row',
        flex: 1,
    },
    rightIconWrapper: {
        marginLeft: rem(8),
    },
    leftIconWrapper: {
        marginRight: rem(8),
    },
    itemInnerContainer: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    defaultPadding: {
        paddingVertical: rem(8),
    },
});
